import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { formatXAF } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Users2 } from "lucide-react";

export default function Equipes() {
  const { t } = useI18n();
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [newE, setNewE] = useState({ name: "", role: "", phone: "", daily_rate: "", skills: "" });

  async function load() {
    const r = await api.get("/employees");
    setEmployees(r.data);
  }
  useEffect(() => { load(); }, []);

  async function addEmployee() {
    if (!newE.name) return toast.error("Nom requis");
    try {
      await api.post("/employees", { ...newE, daily_rate: parseFloat(newE.daily_rate || 0) });
      setOpen(false); setNewE({ name: "", role: "", phone: "", daily_rate: "", skills: "" });
      toast.success(t("added")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }
  async function delEmployee(id) {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success(t("deleted")); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  }

  const totalDaily = employees.reduce((s, e) => s + (e.daily_rate || 0), 0);

  return (
    <div className="space-y-6" data-testid="equipes-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">RH</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("employees_page")}</h1>
          <p className="text-muted-foreground mt-1">{employees.length} · {formatXAF(totalDaily)}/{t("per_day")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-employee-btn"><Plus className="w-4 h-4 mr-2" />{t("employees_page")}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("add")} — {t("employees_page")}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>{t("name")}</Label><Input data-testid="emp-name" value={newE.name} onChange={(e) => setNewE({ ...newE, name: e.target.value })} /></div>
              <div><Label>{t("role")}</Label><Input data-testid="emp-role" placeholder="Ouvrier, technicien…" value={newE.role} onChange={(e) => setNewE({ ...newE, role: e.target.value })} /></div>
              <div><Label>{t("phone")}</Label><Input value={newE.phone} onChange={(e) => setNewE({ ...newE, phone: e.target.value })} /></div>
              <div><Label>{t("daily_rate")} (FCFA)</Label><Input type="number" data-testid="emp-rate" value={newE.daily_rate} onChange={(e) => setNewE({ ...newE, daily_rate: e.target.value })} /></div>
              <div><Label>{t("skills")}</Label><Input value={newE.skills} onChange={(e) => setNewE({ ...newE, skills: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={addEmployee} data-testid="emp-save">{t("save")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
        {employees.map((e) => (
          <Card key={e.id} className="card-hover border-border" data-testid={`emp-card-${e.id}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                    <Users2 className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-semibold">{e.name}</div>
                    <div className="text-xs text-muted-foreground">{e.role}</div>
                  </div>
                </div>
                <Badge variant="secondary">{formatXAF(e.daily_rate)}/j</Badge>
              </div>
              <div className="mt-3 text-sm text-muted-foreground space-y-0.5">
                <div>{e.phone}</div>
                <div>{e.skills}</div>
              </div>
              <div className="mt-3 flex justify-end">
                <Button variant="ghost" size="sm" onClick={() => delEmployee(e.id)}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {employees.length === 0 && <div className="text-sm text-muted-foreground col-span-3 text-center py-8">{t("no_data")}</div>}
      </div>
    </div>
  );
}
